import React, { useEffect, useMemo, useState } from 'react';

import { notification } from 'antd';

import { AGGREGATION_FIELDS, formatServiceTypeDisplayName } from '@/shared/api/openmetadata';

import type { FilterOption } from '@/entities/discovery';

import { fetchEntityTypeChildren } from '@/features/discovery/api/treeApi';

import { useAssetTreeData } from './useAssetTreeData';

type AggregationBucket = {
  key: string;
  doc_count: number;
};

const DATA_SOURCE_ERROR_KEY = 'data-source-error-all';

export const useDataSourceOptions = (selectedKeys: string[]) => {
  const { initialAggregations, isLoading: isInitialLoading } = useAssetTreeData();
  const [rawOptions, setRawOptions] = useState<FilterOption[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const [retryTrigger, setRetryTrigger] = useState(0);

  useEffect(() => {
    if (!initialAggregations) return;

    const loadServiceChildren = async () => {
      setIsLoadingChildren(true);

      try {
        const serviceTypeBuckets = initialAggregations[AGGREGATION_FIELDS.SERVICE_TYPE]?.buckets || [];

        const serviceTypePromises = serviceTypeBuckets.map(async (bucket) => {
          const serviceType = bucket.key;
          const displayName = formatServiceTypeDisplayName(serviceType);

          const response = await fetchEntityTypeChildren({
            entityType: serviceType,
            index: 'dataAsset',
            useAggregation: true,
            serviceType,
          });

          const serviceBuckets = response.aggregations?.[AGGREGATION_FIELDS.SERVICE_NAME]?.buckets || [];

          const children: FilterOption[] = serviceBuckets
            .filter((serviceBucket: AggregationBucket) => {
              return serviceBucket.key.toLowerCase() !== serviceType.toLowerCase();
            })
            .map((serviceBucket: AggregationBucket) => ({
              id: serviceBucket.key,
              title: serviceBucket.key,
              label: serviceBucket.key,
              value: serviceBucket.key,
              count: serviceBucket.doc_count,
              disabled: serviceBucket.doc_count === 0,
            }));

          return {
            id: serviceType,
            title: displayName,
            label: displayName,
            value: serviceType,
            count: bucket.doc_count,
            disabled: bucket.doc_count === 0,
            children: children.length > 0 ? children : undefined,
          };
        });

        const loadedOptions = await Promise.all(serviceTypePromises);
        setRawOptions(loadedOptions);
      } catch (error) {
        console.error('[DataSourceDropdown] Failed to load service children:', error);
        setRawOptions([]);

        const handleRetry = () => {
          notification.destroy(DATA_SOURCE_ERROR_KEY);
          setRetryTrigger((prev) => prev + 1);
        };

        notification.error({
          key: DATA_SOURCE_ERROR_KEY,
          message: '트리 데이터 로드 실패',
          description: '트리 데이터를 불러오지 못했습니다. 다시 시도해 주세요.',
          duration: 0,
          placement: 'bottomLeft',
          btn: React.createElement('button', { onClick: handleRetry }, '다시 시도'),
        });
      } finally {
        setIsLoadingChildren(false);
      }
    };

    loadServiceChildren();
  }, [initialAggregations, retryTrigger]);

  const sortedOptions = useMemo(() => {
    return [...rawOptions].sort((a, b) => {
      const aSelected = selectedKeys.includes(a.id);
      const bSelected = selectedKeys.includes(b.id);

      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      if (a.disabled && !b.disabled) return 1;
      if (!a.disabled && b.disabled) return -1;

      return a.label.localeCompare(b.label);
    });
  }, [rawOptions, selectedKeys]);

  return { options: sortedOptions, isLoading: isInitialLoading || isLoadingChildren };
};
